import { Button, Dialog, DialogClose, InlineAlert } from "@/components";
import { formatDateTime, formatDuration } from "@/lib/format";
import { useNow } from "@/lib/hooks";
import type { Job } from "./api";
import styles from "./jobs.module.css";

function json(value: unknown): string {
  if (value === null || value === undefined) return "—";
  return JSON.stringify(value, null, 2);
}

/** Everything about one job: why it failed, timing, attempts, payload, result and the full error. */
export function JobDetailsDialog({
  job,
  description,
  onClose,
  onRequeue,
  requeuePending = false,
}: {
  job: Job | null;
  description?: string;
  onClose: () => void;
  /** Offers "Retry job"; pass it only when this job can run again and the user may start it. */
  onRequeue?: (job: Job) => void;
  requeuePending?: boolean;
}) {
  const now = useNow(1000);
  const duration = job?.started_at
    ? (job.finished_at ? new Date(job.finished_at).getTime() : now) - new Date(job.started_at).getTime()
    : null;
  const errorTitle =
    !job?.last_error_summary || job.status === "cancelled"
      ? null
      : job.status === "failed"
        ? "This job failed"
        : "Earlier attempt";
  return (
    <Dialog
      open={job !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={description ?? job?.kind ?? "Job"}
      description={job ? job.kind : undefined}
      size="lg"
      footer={
        <>
          <DialogClose>Close</DialogClose>
          {job && onRequeue ? (
            <Button
              variant="primary"
              icon="refresh"
              pending={requeuePending}
              pendingLabel="Retrying…"
              onClick={() => onRequeue(job)}
            >
              Retry job
            </Button>
          ) : null}
        </>
      }
    >
      {job ? (
        <>
          {errorTitle ? (
            <InlineAlert tone={job.status === "failed" ? "danger" : "info"} title={errorTitle}>
              <span className={styles.errorSummary} translate="no">
                {job.last_error_summary}
              </span>
            </InlineAlert>
          ) : null}
          <dl className={styles.facts}>
            <div>
              <dt>Status</dt>
              <dd>
                {job.status}
                {job.cancel_requested && job.status === "running" ? " (cancel requested)" : ""}
              </dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>{formatDateTime(job.created_at)}</dd>
            </div>
            <div>
              <dt>Started</dt>
              <dd>{formatDateTime(job.started_at)}</dd>
            </div>
            <div>
              <dt>Finished</dt>
              <dd>{formatDateTime(job.finished_at)}</dd>
            </div>
            <div>
              <dt>Duration</dt>
              <dd>{formatDuration(duration)}</dd>
            </div>
            <div>
              <dt>Attempts</dt>
              <dd>
                {job.attempts} of {job.max_attempts}
              </dd>
            </div>
            {job.schedule_name ? (
              <div>
                <dt>Schedule</dt>
                <dd>{job.schedule_name}</dd>
              </div>
            ) : null}
          </dl>
          {job.last_error ? (
            <section className={styles.block}>
              <h3 className={styles.blockTitle}>Full error</h3>
              <pre className={styles.code} data-tone="danger" translate="no">
                {job.last_error}
              </pre>
            </section>
          ) : null}
          <section className={styles.block}>
            <h3 className={styles.blockTitle}>Payload</h3>
            <pre className={styles.code}>{json(job.payload)}</pre>
          </section>
          <section className={styles.block}>
            <h3 className={styles.blockTitle}>Result</h3>
            <pre className={styles.code}>{json(job.result)}</pre>
          </section>
        </>
      ) : null}
    </Dialog>
  );
}
